import React from 'react';
import { API } from '../api';
import PhotoPermission, { usePhotoPermission } from './PhotoPermission';
import { fileToDataUrl } from './GalleryTab';

/*
 * A gallery photo as the poster for one event or competition, chosen from
 * where the admin is looking at that event or competition.
 *
 * The gallery can do the same from the other end — pick the photo, then the
 * event. Both land on /api/posters/from-photo, which makes an ordinary poster
 * row, so the home page, the What's on page and the club screen show it the
 * same way they show a generated one.
 *
 * A new photo can go up from here too, into the gallery like any other,
 * because "the flyer is on my phone" should not mean a trip to another tab.
 */
function PhotoPosterPicker({ clubId, eventId, competitionId, subjectName, onClose, onDone }) {
  const [photoOk, setPhotoOk] = usePhotoPermission();
  const [photos, setPhotos] = React.useState(null);
  const [chosen, setChosen] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(false);

  const qs = clubId ? '?clubId=' + clubId : '';

  const load = React.useCallback(async () => {
    const r = await fetch(`${API}/api/gallery${qs}`);
    if (!r.ok) { setError('Could not load the gallery.'); setPhotos([]); return; }
    const data = await r.json().catch(() => ({}));
    // Logos are badges, not posters.
    setPhotos((data.photos || []).filter(p => !p.isLogo));
  }, [qs]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const upload = async file => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const data = await fileToDataUrl(file);
      if (!data) { setError('That file could not be read.'); return; }
      const r = await fetch(`${API}/api/gallery`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clubId, data, isLogo: false, label: file.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 100) }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) { setError(out.error || 'That picture would not go in.'); return; }
      await load();
      // The photo just added is the one they meant.
      const id = out.id || (out.photo && out.photo.id);
      if (id) setChosen(id);
    } finally { setBusy(false); }
  };

  const attach = async () => {
    if (!chosen) return;
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/posters/from-photo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photoId: chosen, eventId: eventId || undefined, competitionId: competitionId || undefined }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) { setError(out.error || 'That photo could not be made into a poster.'); return; }
      setDone(true);
      if (onDone) onDone(out.poster);
    } finally { setBusy(false); }
  };

  return (
    <div className="w3-modal" style={{ display: 'block', zIndex: 1900 }} onClick={onClose}>
      <div className="w3-modal-content w3-card-4 w3-round photo-poster-picker"
        role="dialog" aria-modal="true" aria-label={`Poster for ${subjectName}`}
        onClick={e => e.stopPropagation()}>
        <div className="photo-poster-head">
          <h4>Poster for {subjectName}</h4>
          <button type="button" className="w3-button w3-round" onClick={onClose} aria-label="Close">
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="photo-poster-body">
          {done ? (
            <p>
              Done — that photo is now the poster for <strong>{subjectName}</strong>. It shows on the
              home page and the club screen, and comes down once it is over. Switch it off or delete it
              under Posters.
            </p>
          ) : (
            <>
              <p className="photo-poster-note">
                Choose a photo from the club&rsquo;s gallery — a flyer, or a picture of the one on the
                noticeboard — or add a new one.
              </p>

              <PhotoPermission ok={photoOk} onChange={setPhotoOk} id="photo-poster-permission" />
              <label className={`w3-button w3-border w3-round${busy || !photoOk ? ' gal-disabled' : ''}`}
                title={photoOk ? undefined : 'Tick the box above first'}>
                <i className="fa fa-camera w3-margin-right"></i>Add a new photo
                <input type="file" accept="image/*" disabled={busy || !photoOk} style={{ display: 'none' }}
                  onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
              </label>

              {photos === null ? (
                <p className="photo-poster-note">Loading the gallery…</p>
              ) : photos.length === 0 ? (
                <p className="photo-poster-note">No photos in the gallery yet. Add one above.</p>
              ) : (
                <div className="photo-poster-grid">
                  {photos.map(p => (
                    <button key={p.id} type="button"
                      className={`photo-poster-pick${chosen === p.id ? ' is-chosen' : ''}`}
                      aria-pressed={chosen === p.id}
                      onClick={() => setChosen(p.id)}>
                      <img src={p.thumb} alt="" loading="lazy" />
                      <span>{p.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {error && <div className="poster-error" role="alert">{error}</div>}
        </div>

        <div className="photo-poster-foot">
          {!done && (
            <button type="button" className="w3-button w3-round poster-btn-primary"
              disabled={!chosen || busy} onClick={attach}>
              {busy ? 'Saving…' : 'Use as poster'}
            </button>
          )}
          <button type="button" className="w3-button w3-round poster-btn" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoPosterPicker;
